import React, { useEffect, useRef } from "react";

import { cn } from "../../../common/u1/cn";
import { SelectField } from "../../../common/u1/SelectField";
import {
  EditSquareIcon,
  UploadIcon,
  FileIcon,
  ChartIcon,
} from "../../../../assets/ui/icons";
import FinancialCharts from "../../../common/FinancialCharts";
import { EditSectionLayout } from "./EditSectionLayout";
import { EditFooterBar } from "./EditFooterBar";
import { SectionNotice } from "./SectionNotice";
import { FinancialMethodCard } from "./FinancialMethodCard";
import { FinancialMethodBar } from "./FinancialMethodBar";
import { ManualFinancialsPanel } from "./ManualFinancialsPanel";
import { FinancialDocumentsPanel } from "./FinancialDocumentsPanel";
import { FinancialCsvPanel } from "./FinancialCsvPanel";
import { useFinancialSetup } from "./useFinancialSetup";
import { useManualFinancials } from "./useManualFinancials";
import { useFinancialDocumentUploads } from "./useFinancialDocumentUploads";
import { useFinancialCsv } from "./useFinancialCsv";
import {
  CURRENCY_OPTIONS,
  FINANCIAL_METHODS,
  METHOD_CSV,
  METHOD_DOCUMENTS,
  METHOD_GRAPHS,
  METHOD_MANUAL,
  UNIT_OPTIONS,
  findMethod,
} from "./financialSetupFields";

/* Section 16 — Financial Documents (the live "Financial Setup"). */

/* The design's own glyphs: a pencil over a square for the blank form, the
   upload tray, and a bare page for the template. */
const METHOD_ICONS = {
  [METHOD_MANUAL]: EditSquareIcon,
  [METHOD_DOCUMENTS]: UploadIcon,
  [METHOD_CSV]: FileIcon,
  [METHOD_GRAPHS]: ChartIcon,
};

export const FinancialDocumentsSection = ({
  onNext,
  onPrev,
  onSkip,
  header,
  className = "",
}) => {
  const setup = useFinancialSetup();
  const {
    allowed,
    companyName,
    openMethod,
    openMethodPanel,
    closeMethodPanel,
    basis,
    setBasisField,
    basisLocked,
    basisError,
    hasChartData,
    chartData,
  } = setup;

  /* All three hooks are created unconditionally — hooks cannot be called
     behind a branch — and only the open one is rendered. */
  const manual = useManualFinancials({ onDone: onNext });
  const uploads = useFinancialDocumentUploads({ onDone: onNext });
  const csv = useFinancialCsv({ maxYear: chartData?.max_year, onDone: onNext });

  const method = findMethod(openMethod);

  /* Manual entry is a WIZARD, not a form: it walks the years newest-first, and its footer is the live screen's — Skip on the newest year, Previous elsewhere,... */
  const manualWizard = openMethod === METHOD_MANUAL;

  /* Each method owns its save; with none open, Save & Continue just moves on,
     as the live chooser's Next does. */
  const onContinue = async () => {
    if (openMethod === METHOD_MANUAL) {
      if (manual.atSaveStage) await manual.save();
      else manual.next();
      return;
    }
    if (openMethod === METHOD_DOCUMENTS) {
      await uploads.save();
      return;
    }
    if (openMethod === METHOD_CSV) {
      await csv.save();
      return;
    }
    onNext?.();
  };

  const busy = manual.busy || uploads.busy || csv.busy;

  const cards = FINANCIAL_METHODS.filter(
    (m) => !m.graphsOnly || hasChartData
  );

  /* Selecting a method opens its form BELOW the chooser, which on a short viewport is off screen — the card highlighted and nothing else appeared to happen. */
  const panelRef = useRef(null);
  useEffect(() => {
    if (!openMethod) return;
    const node = panelRef.current;
    if (typeof node?.scrollIntoView === "function") {
      node.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [openMethod]);

  return (
    <EditSectionLayout
      className={className}
      header={header}
      footer={
        <EditFooterBar
          onBack={onPrev}
          /* While the wizard is open the middle control is its own: "Skip" on
             the newest year, "Previous" on any other. */
          onSkip={manualWizard ? manual.back : onSkip}
          skipLabel={
            manualWizard
              ? manual.onNewestYear
                ? "Skip"
                : "Previous"
              : undefined
          }
          onContinue={onContinue}
          continueLabel={
            manualWizard && !manual.atSaveStage ? "Next" : undefined
          }
          isLoading={busy}
        />
      }
    >
      <h2 className="m-0 text-[16px] font-bold text-[var(--ink)] font-sans">
        Financial Setup
      </h2>

      {/* Transcribed gate: under three years in business, no method is
          available. The live screen blurs the cards behind a padlock. */}
      {!allowed && (
        <SectionNotice className="mt-3.5" tone="info" title="Not available yet">
          {companyName} is less than 3 years in business, so financial figures
          cannot be added yet.
        </SectionNotice>
      )}

      <div
        className={cn(
          "mt-3.5 rounded-[14px] border border-[var(--line)] bg-white",
          "p-4 sm:p-6"
        )}
      >
        {/* The BASIS comes first, because openMethodPanel refuses a method until it is set ("Choose a currency and an amount unit first"). */}
        <p className="m-0 text-[13.5px] font-bold text-[var(--ink)] font-sans">
          1 · Set your reporting basis
        </p>

        <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <SelectField
            label="Currency"
            labelVariant="field"
            id="u1-fin-currency"
            value={basis.currency}
            placeholder="Select a currency…"
            options={CURRENCY_OPTIONS}
            /* Transcribed: locked once the figures behind them are verified. */
            disabled={basisLocked}
            onChange={(e) => setBasisField("currency", e.target.value)}
          />
          <SelectField
            label="Amount Unit"
            labelVariant="field"
            id="u1-fin-units"
            value={basis.units}
            placeholder="Select a unit…"
            options={UNIT_OPTIONS}
            disabled={basisLocked}
            onChange={(e) => setBasisField("units", e.target.value)}
          />
        </div>

        {/* Both selects start empty, so a locked pair would otherwise read as
            two fields nobody filled in and nobody can. */}
        {basisLocked && (
          <p className="m-0 mt-2.5 text-[12px] text-[var(--muted)] font-sans">
            Your reporting basis is already verified and can&apos;t be changed
            here.
          </p>
        )}

        {basisError && (
          <p
            role="alert"
            className="m-0 mt-2.5 text-[12.5px] font-semibold text-[var(--coral-deep)] font-sans"
          >
            {basisError}
          </p>
        )}

        <p className="m-0 mt-5 text-[13.5px] font-bold text-[var(--ink)] font-sans">
          2 · Select how you want to proceed
        </p>

        <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map((m) => (
            <FinancialMethodCard
              key={m.id}
              method={m}
              Icon={METHOD_ICONS[m.id]}
              selected={openMethod === m.id}
              locked={!allowed}
              lockedReason={`${companyName} is less than 3 years in business. Hence you do not have this option active.`}
              onSelect={() => openMethodPanel(m.id)}
            />
          ))}
        </div>

      </div>

      {method && (
        <div ref={panelRef}>
          {/* The design's dashed rule between the chooser and what it opened. */}
          <div
            aria-hidden="true"
            className="mt-6 border-t border-dashed border-[var(--line-2)]"
          />

          <FinancialMethodBar
            className="mt-6"
            method={method}
            Icon={METHOD_ICONS[method.id]}
            onChange={closeMethodPanel}
          />

          {openMethod === METHOD_MANUAL && (
            <ManualFinancialsPanel className="mt-4" manual={manual} />
          )}
          {openMethod === METHOD_DOCUMENTS && (
            <FinancialDocumentsPanel className="mt-4" uploads={uploads} />
          )}
          {openMethod === METHOD_CSV && (
            <FinancialCsvPanel className="mt-4" csv={csv} />
          )}
          {openMethod === METHOD_GRAPHS && (
            <div className="mt-4">
              {/* The existing chart component, unchanged — rebuilding the charts is not this ticket's job. */}
              <FinancialCharts
                edit
                resData={chartData}
                setShowUploadInput={() => {}}
              />
            </div>
          )}
        </div>
      )}
    </EditSectionLayout>
  );
};

export default FinancialDocumentsSection;
